import { IColumn } from "@fluentui/react";
import { ReactElement } from "react";

import { ListButtonRenderProps } from "./ListButtonRenderProps";
import { renderButtons, renderLink, renderText } from "./renderFunctions";

export type ListTableColumn<T> = Pick<
    IColumn,
    "name" | "minWidth" | "maxWidth" | "isResizable" | "isMultiline" | "isSorted" | "isSortedDescending"
> & {
    key: string;
    buttons?: ListButtonRenderProps<T>[];
    isSortable?: boolean;
    maxLenght?: number;
    openInNewTab?(item: T): boolean;
    isDisabled?(item: T): boolean;
    isAnnotated?(item: T): boolean | string;
    getHref?(item: T): string | null;
    getTooltipText?(item: T): string;
    onItemInvoked?(item: T): void;
    onRender?(item?: T, index?: number, column?: ListTableColumn<T>): ReactElement | null;
};

export const createListTableColumns = <T>(columns: TableColumn<T>[]): ListTableColumn<T>[] => columns.map(createColumn);

const createColumn = <T>(column: TableColumn<T>) => {
    switch (column.type) {
        case "basic": {
            return createBasicColumn(column);
        }

        case "interactive": {
            return createInteractiveColumn(column);
        }

        case "link": {
            return createLinkColumn(column);
        }

        case "buttons": {
            return createButtonColumn(column);
        }

        default:
            throw new Error("Unsupported column type.");
    }
};

const createBasicColumn = <T>(column: BasicTableColumn<T>) => ({
    ...createColumnBase(column),
    onRender: column.onRender ?? renderText,
    isAnnotated: column.isAnnotated,
    maxLenght: column.maxLenght
});

const createInteractiveColumn = <T>(column: InteractiveTableColumn<T>) => ({
    ...createColumnBase(column),
    onRender: column.onRender,
    onItemInvoked: column.onItemInvoked,
    isDisabled: column.isDisabled,
    isAnnotated: column.isAnnotated
});

const createLinkColumn = <T>(column: LinkTableColumn<T>) => ({
    ...createColumnBase(column),
    onRender: renderLink,
    openInNewTab: column.openInNewTab,
    getHref: column.getHref,
    isDisabled: column.isDisabled,
    isAnnotated: column.isAnnotated,
    getTooltipText: column.getTooltipText
});

const createButtonColumn = <T>(column: ButtonsTableColumn<T>) => ({
    ...createColumnBase(column),
    onRender: renderButtons,
    buttons: column.buttons
});

const createColumnBase = <T>(column: TableColumn<T>) => ({
    key: column.key.toString(),
    name: column.name,
    minWidth: column.minWidth,
    maxWidth: column.maxWidth,
    isResizable: column.isResizable ?? true,
    isSortable: column.isSortable,
    isMultiline: column.isMultiline
});

export type TableColumn<T> =
    | BasicTableColumn<T>
    | InteractiveTableColumn<T>
    | LinkTableColumn<T>
    | ButtonsTableColumn<T>;

interface TableColumnBase<T> {
    key: keyof T | "actions";
    name: string;
    minWidth: number;
    maxWidth?: number;
    isResizable?: boolean;
    isSortable?: boolean;
    isMultiline?: boolean;
}

type BasicTableColumn<T> = TableColumnBase<T> & {
    type: "basic";
    maxLenght?: number;
    onRender?(item?: T, index?: number, column?: ListTableColumn<T>): ReactElement | null;
    isAnnotated?(item: T): boolean | string;
};

type InteractiveTableColumn<T> = TableColumnBase<T> & {
    type: "interactive";
    onItemInvoked(item: T): void;
    onRender?(item?: T, index?: number, column?: ListTableColumn<T>): ReactElement | null;
    isDisabled?(item: T): boolean;
    isAnnotated?(item: T): boolean | string;
};

type LinkTableColumn<T> = TableColumnBase<T> & {
    type: "link";
    openInNewTab?(item: T): boolean;
    getHref(item: T): string | null;
    isDisabled?(item: T): boolean;
    isAnnotated?(item: T): boolean | string;
    getTooltipText?(item: T): string;
};

type ButtonsTableColumn<T> = TableColumnBase<T> & {
    type: "buttons";
    buttons: ListButtonRenderProps<T>[];
};
